/**
 * Iter-225p — Role Confirmation Modal
 *
 * Asks imported / sparse-profile talents to confirm their current roles.
 * Brand Ambassador is pre-checked for CSV-imported users (per business
 * context: most StaffConnect talents were Brand Ambassadors). Historical
 * bookings/applications surface as soft suggestions, never auto-saved.
 *
 * AI Avatar Creator is intentionally NOT in the list — that's a separate
 * contractor category, not a talent role.
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Loader2, CheckCircle2, Info, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function RoleConfirmationModal() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [other, setOther] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/talent/roles-confirmation/status`, { headers: headers() });
        if (!res.ok) { setLoadError(true); return; }
        const body = await res.json();
        setStatus(body);
        if (body.needs_confirmation) {
          setPicked(new Set(body.suggested_roles || []));
          setOpen(true);
        }
      } catch {
        setLoadError(true);
      }
    })();
  }, []);

  if (loadError || !status || !open) return null;

  const toggle = (role) => {
    const next = new Set(picked);
    if (next.has(role)) next.delete(role); else next.add(role);
    setPicked(next);
  };

  const submit = async ({ skipped = false } = {}) => {
    setSubmitting(true);
    try {
      const body = {
        roles: Array.from(picked),
        skipped,
        other: other.trim() || null,
      };
      const res = await fetch(`${API}/api/talent/roles-confirmation/confirm`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not save roles');
        return;
      }
      toast.success(
        skipped
          ? 'No problem — you can update your roles any time from your profile.'
          : `Saved ${data.saved_roles.length} role${data.saved_roles.length === 1 ? '' : 's'} to your profile.`
      );
      setOpen(false);
    } catch {
      toast.error('Could not save roles');
    } finally {
      setSubmitting(false);
    }
  };

  const isHistory = (role) => (status.history_roles || []).includes(role);
  const isCurrent = (role) => (status.current_roles || []).includes(role);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
      data-testid="role-confirmation-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-br from-indigo-600 to-fuchsia-600 px-5 py-4 text-white relative">
          <button
            onClick={() => submit({ skipped: true })}
            disabled={submitting}
            className="absolute top-3 right-3 rounded-full p-1 hover:bg-white/20 transition"
            data-testid="role-confirmation-skip-x"
            aria-label="Skip"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">
            <Sparkles className="w-3 h-3" />
            One-time setup
          </div>
          <h2 className="mt-2 text-lg font-semibold leading-tight">Confirm your roles</h2>
          <p className="mt-1 text-sm text-white/90 leading-snug">
            {status.is_csv_imported
              ? 'We imported your profile from StaffConnect.'
              : 'Help us match you to the right opportunities.'}{' '}
            Pick every role you want to be considered for.
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          {status.ba_pre_checked_reason && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs leading-snug" data-testid="ba-presuggest-banner">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{status.ba_pre_checked_reason}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="role-checkbox-grid">
            {(status.available_roles || []).map((role) => {
              const isChecked = picked.has(role);
              return (
                <label
                  key={role}
                  className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 cursor-pointer transition ${
                    isChecked
                      ? 'border-indigo-300 bg-indigo-50/70'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  data-testid={`role-option-${role.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggle(role)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 leading-tight">{role}</div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {isCurrent(role) && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 uppercase tracking-wide">
                          <CheckCircle2 className="w-2.5 h-2.5" /> Current
                        </span>
                      )}
                      {isHistory(role) && !isCurrent(role) && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold text-blue-700 uppercase tracking-wide">
                          Past activity
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div>
            <label htmlFor="role-other" className="text-xs font-medium text-gray-700">
              Other (optional)
            </label>
            <Input
              id="role-other"
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Anything we missed?"
              className="mt-1 text-sm"
              data-testid="role-other-input"
              maxLength={120}
            />
          </div>

          <p className="text-[11px] text-gray-500 leading-snug">
            You can change these any time from your profile. AI Avatar Creator is a separate contractor track and is intentionally not listed here.
          </p>
        </div>

        <div className="bg-gray-50 px-5 py-3 flex items-center gap-2 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={() => submit({ skipped: true })}
            disabled={submitting}
            className="text-gray-600"
            data-testid="role-confirmation-skip-btn"
          >
            Skip for now
          </Button>
          <div className="flex-1" />
          <Button
            onClick={() => submit({ skipped: false })}
            disabled={submitting || picked.size === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="role-confirmation-save-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save {picked.size > 0 ? `(${picked.size})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
